import React, { useState, useEffect, useCallback } from "react";
import { downloadInquiryReport, getRetailerInquiries, getRetailerInquiry } from "../services/api"; // Adjust path as needed
import { InquiryDetailsModal } from "../components/InquiryDetailsModal";
import RetailerSidebar from "../components/retailer/RetailerSidebar";
import { showErrorToast, showSuccessToast } from "../services/toast";

export default function ProductInquiriesPage() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Pagination & Filter States
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [country, setCountry] = useState("");
  const [durationFilter, setDurationFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("All Sources");
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState("");

  const handleViewDetails = async (inquiryId) => {
    setIsModalOpen(true);
    setModalLoading(true);
    setModalError("");
    setSelectedInquiry(null);

    try {
            const token = localStorage.getItem('retailer_token');

      const data = await getRetailerInquiry(inquiryId);
      setSelectedInquiry(data.data || data);
    } catch (err) {
      setModalError(err.message || "Failed to load inquiry details.");
    } finally {
      setModalLoading(false);
    }
  };

  // Meta state for total records/pages returned by API
  const [paginationMeta, setPaginationMeta] = useState({
    total: 0,
    totalPages: 1,
  });

  // Overview stats state (can be updated dynamically if returned by your API)
  const [stats, setStats] = useState({
    totalInquiries: 0,
    totalAbandoned: 0,
    todayInquiries: 0,
    todayAbandoned: 0,
    thisMonthInquiries: 0,
    thisMonthAbandoned: 0,
    lastMonthInquiries: 0,
    lastMonthAbandoned: 0,
  });

  const fetchInquiries = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
    const token = localStorage.getItem('retailer_token');
      const response = await getRetailerInquiries({
        page,
        limit,
        search,
        country: country === "All Country" ? "" : country,
        });

      // Adjust based on your API response structure (e.g., response.data, response.items)
      const data = response.data || response.items || response;
      const totalCount = response.total || response.total_count || data.length;
      const totalPages = Math.ceil(totalCount / limit) || 1;

      setInquiries(Array.isArray(data) ? data : []);
      setPaginationMeta({
        total: totalCount,
        totalPages,
      });

      if (response.stats) {
        setStats(response.stats);
      }
    } catch (err) {
      setError(err.message || "Failed to load inquiries.");
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, country]);

  // Fetch data on dependency changes
  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  const handleResetFilters = () => {
    setCountry("");
    setDurationFilter("");
    setSourceFilter("All Sources");
    setSearch("");
    setPage(1);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return isNaN(date.getTime())
      ? dateStr
      : date.toLocaleString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
  };

  const [downloading, setDownloading] = useState(false);

  const handleDownloadReport = async () => {
    setDownloading(true);
    try {
      await downloadInquiryReport();
      showSuccessToast("Inquiry report downloaded successfully!");
    } catch (err) {
      showErrorToast(
        err?.message || "Failed to download report. Please try again."
      );
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50/50">
      {/* Header */}
      <RetailerSidebar />
      <main className="ml-64 p-6 w-full">
      <div className="mb-6 flex items-center justify-between">  
        <h1 className="text-sm font-bold uppercase tracking-wider text-gray-900">
            Product Inquiries Overview
          </h1>
          <button
      type="button"
      onClick={handleDownloadReport}
      disabled={downloading}
      className="rounded-md bg-indigo-600 px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
    >
      {downloading ? (
        <>
          <svg
            className="animate-spin h-3.5 w-3.5 text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          Downloading...
        </>
      ) : (
        "Download Report"
      )}
    </button>
        </div>

      {/* Overview Cards */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Card 1 */}
        <div className="flex items-center gap-4 rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-500">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div className="flex flex-1 justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-700">Total Inquiries</p>
              <p className="text-xl font-bold text-gray-900">{stats.totalInquiries || paginationMeta.total}</p>
            </div>
            <div className="text-right">
              <p className="text-xs font-semibold text-gray-700">Abandoned</p>
              <p className="text-xl font-bold text-gray-900">{stats.totalAbandoned}</p>
            </div>
          </div>
        </div>

        {/* Card 2 */}
        <div className="flex items-center gap-4 rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50 text-emerald-500">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div className="flex flex-1 justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-700">Today's Inquiries</p>
              <p className="text-xl font-bold text-gray-900">{stats.todayInquiries}</p>
            </div>
            <div className="text-right">
              <p className="text-xs font-semibold text-gray-700">Abandoned</p>
              <p className="text-xl font-bold text-gray-900">{stats.todayAbandoned}</p>
            </div>
          </div>
        </div>

        {/* Card 3 */}
        <div className="flex items-center gap-4 rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-50 text-sky-500">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div className="flex flex-1 justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-700">This Month</p>
              <p className="text-xl font-bold text-gray-900">{stats.thisMonthInquiries}</p>
            </div>
            <div className="text-right">
              <p className="text-xs font-semibold text-gray-700">Abandoned</p>
              <p className="text-xl font-bold text-gray-900">{stats.thisMonthAbandoned}</p>
            </div>
          </div>
        </div>

        {/* Card 4 */}
        <div className="flex items-center gap-4 rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-50 text-amber-500">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div className="flex flex-1 justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-700">Last Month</p>
              <p className="text-xl font-bold text-gray-900">{stats.lastMonthInquiries}</p>
            </div>
            <div className="text-right">
              <p className="text-xs font-semibold text-gray-700">Abandoned</p>
              <p className="text-xl font-bold text-gray-900">{stats.lastMonthAbandoned}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="relative mb-6 rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
        <h2 className="mb-4 text-xs font-bold text-gray-900">Filter</h2>
        <button
          type="button"
          onClick={handleResetFilters}
          className="absolute right-4 top-4 rounded-full p-1 text-red-500 hover:bg-red-50"
          title="Reset filters"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-600">Select Country</label>
            <select
              value={country}
              onChange={(e) => {
                setCountry(e.target.value);
                setPage(1);
              }}
              className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 outline-none focus:border-indigo-500"
            >
              <option value="">All Country</option>
              <option value="India">India</option>
              <option value="USA">USA</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-600">Duration Type</label>
            <select
              value={durationFilter}
              onChange={(e) => setDurationFilter(e.target.value)}
              className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 outline-none focus:border-indigo-500"
            >
              <option value="">Select Duration Type</option>
              <option value="today">Today</option>
              <option value="this_month">This Month</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-600">Inquiry Source</label>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 outline-none focus:border-indigo-500"
            >
              <option value="All Sources">All Sources</option>
              <option value="Visualizer">Visualizer</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main Table */}
      <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
        <div className="mb-4 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <span>Show</span>
            <select
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
              className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 outline-none focus:border-indigo-500"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span>entries</span>
          </div>

          <div className="w-full sm:w-72">
            <input
              type="text"
              placeholder="Search order by name, phone, or email..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full rounded-md border border-gray-200 px-3 py-1.5 text-xs text-gray-700 outline-none focus:border-indigo-500"
            />
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 p-3 text-xs text-red-600">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto w-full">
          <table className="w-full border-collapse text-left text-xs">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50 text-gray-600">
                <th className="py-3 px-4 font-semibold">Id</th>
                <th className="py-3 px-4 font-semibold">Full Name/Company Name</th>
                <th className="py-3 px-4 font-semibold">Order Information</th>
                <th className="py-3 px-4 font-semibold">Contact Information</th>
                <th className="py-3 px-4 font-semibold">Address</th>
                <th className="py-3 px-4 font-semibold">Date & Time</th>
                <th className="py-3 px-4 text-center font-semibold">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="7" className="py-8 text-center text-gray-500">
                    Loading inquiries...
                  </td>
                </tr>
              ) : inquiries.length === 0 ? (
                <tr>
                  <td colSpan="7" className="py-8 text-center text-gray-500">
                    No inquiries found.
                  </td>
                </tr>
              ) : (
                inquiries.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50/50">
                    <td className="py-4 px-4 font-medium text-gray-900">{row.inquiry_id}</td>
                    <td className="py-4 px-4">{row.full_name || row.company || "-"}</td>
                    <td className="py-4 px-4">
                      <div>
                        {/* <div>Name: {row.full_name || row.customer_name || row.name || "-"}</div> */}
                        <div>Total Price: {row.total_price ?? "0.00"}</div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div>
                        <div>Phone No: {row.contact_country_code || ""} {row.contact_number || row.phone || "-"}</div>
                        <div>Email No: {row.email || row.customer_email || "-"}</div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div>
                        <div>Country : {row.country || "null"}</div>
                        <div>State : {row.state || "null"}</div>
                        <div>City : {row.city || "null"}</div>
                        <div>Address : {row.address || ""}</div>
                      </div>
                    </td>
                    <td className="py-4 px-4 whitespace-nowrap">
                      {formatDate(row.created_at || row.dateTime)}
                    </td>
                    <td className="py-4 px-4 text-center">
                <button
                  type="button"
                  onClick={() => handleViewDetails(row.id)}
                  className="rounded-md bg-indigo-500 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-indigo-600"
                >
                  View Product Details
                </button>
              </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <InquiryDetailsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        inquiryData={selectedInquiry}
        loading={modalLoading}
        error={modalError}
      />
        </div>

        {/* Footer & Pagination */}
        <div className="mt-4 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
          <p className="text-xs text-gray-500">
            Showing {inquiries.length > 0 ? (page - 1) * limit + 1 : 0} to{" "}
            {Math.min(page * limit, paginationMeta.total)} of {paginationMeta.total} entries
          </p>

          <div className="flex items-center gap-1 text-xs">
            <button
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              disabled={page === 1 || loading}
              className="rounded-md border border-gray-200 px-3 py-1 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="rounded-md bg-indigo-600 px-3 py-1 font-semibold text-white">
              {page}
            </span>
            <button
              onClick={() => setPage((prev) => (prev < paginationMeta.totalPages ? prev + 1 : prev))}
              disabled={page >= paginationMeta.totalPages || loading}
              className="rounded-md border border-gray-200 px-3 py-1 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>
      </main>
    </div>
  );
}